import { t } from '@/lib/i18n';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { AuthLayout } from '@/components/auth-layout';
import { LoginForm } from '@/features/auth/login/form';
import { sessionQueryOptions } from '@/features/auth/session/session-api';
import { useRedirectNavigation } from '@/features/auth/use-redirect-navigation';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { sanitizeRedirect } from '@/lib/routes';

/**
 * The login route: it decides who may see the form, and where to go afterwards.
 *
 * `beforeLoad` reads the session before anything renders — someone already signed in should
 * never see a login form, not even for a frame — and `validateSearch` reduces the return path
 * to a same-origin path, so editing the URL cannot turn the post-login redirect into an open
 * redirect.
 */
export const Route = createFileRoute('/_auth/login')({
  validateSearch: (search: Record<string, unknown>) => ({
    redirect: sanitizeRedirect(search.redirect),
  }),
  beforeLoad: async ({ context }) => {
    const session =
      await context.queryClient.ensureQueryData(sessionQueryOptions);
    if (session !== null) throw redirect({ to: '/' });
  },
  component: LoginPage,
});

function LoginPage() {
  useDocumentTitle(t('登录'));
  const { redirect: redirectTo } = Route.useSearch();
  const goToRedirect = useRedirectNavigation();

  return (
    <AuthLayout
      title={t('欢迎回来')}
      subtitle={t('登录爱出海运营后台')}
    >
      <LoginForm
        redirectTo={redirectTo}
        onAuthenticated={() => goToRedirect(redirectTo)}
      />
    </AuthLayout>
  );
}
